import { Button, LabelledField, ModalAlert, Select } from "@mixitone/components";
import {
  Account,
  TournamentCompetition,
  TournamentEntry,
  TournamentPlayer,
  TournamentRegistration,
} from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { List, ValidationError } from "@mixitone/oom";
import { indexBy, keyBy } from "@mixitone/util";
import { FormController } from "components/Forms/FormController";
import {
  groupStageMatchesComplete,
  playoffMatchesStarted,
  roundRobinGroupsLocked,
  roundRobinPlayoffSettings,
  syncEntryGroupToCompetition,
} from "components/Pages/Tournament/tournamentHelpers";
import routeService from "services/RouteService";
import { getFormat } from "tournament-formats";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";
import type { EntryDataField } from "../../tournament-formats/TournamentFormat";
import { type CustomComponentFieldProps, field } from "./FormFieldsDefinition";
import { TournamentPlayerSearch } from "./TournamentPlayerSearch";

interface PlayerOccupation {
  entryId: string;
  teammateNames: string[];
  playerCount: number;
  requiredPlayers: number;
  hasPlayed: boolean;
}

interface State {
  competitions: List<TournamentCompetition>;
  players: TournamentPlayer[];
  occupationsByCompetition: Record<string, Record<string, PlayerOccupation>>;
  selectedPlayers: TournamentPlayer[];
  searchValue: TournamentPlayer | null | "new";
  pendingOccupiedPlayer: TournamentPlayer | null;
  formatDataSchema: EntryDataField[];
  availableGroups: string[];
  originalDivision: number | undefined;
  groupStageComplete: boolean;
  playoffsHaveStarted: boolean;
}

interface EntryAttributes {
  tournament_competition_id?: string;
  seed?: number;
  name?: string;
  format_data?: Record<string, any>;
}

interface Props {
  tournamentSessionId: string;
  id?: string;
  attributes?: EntryAttributes;
}

function buildOccupationsByCompetition(
  competitions: TournamentCompetition[],
  players: TournamentPlayer[],
  registrations: TournamentRegistration[],
  entries: TournamentEntry[],
): Record<string, Record<string, PlayerOccupation>> {
  const playersById = keyBy(
    players.filter((player) => player.id),
    (player) => player.id!,
  );
  const entriesById = keyBy(
    entries.filter((entry) => entry.id),
    (entry) => entry.id!,
  );
  const registrationsByEntry = indexBy(
    registrations.filter(
      (registration) => registration.tournament_entry_id && registration.tournament_player_id,
    ),
    "tournament_entry_id",
  );

  const result: Record<string, Record<string, PlayerOccupation>> = {};
  for (const competition of competitions) {
    if (competition.id) result[competition.id] = {};
  }

  for (const [entryId, entryRegistrations] of Object.entries(registrationsByEntry)) {
    const competitionId = entryRegistrations[0]?.tournament_competition_id;
    if (!competitionId || !result[competitionId]) continue;
    const competition = competitions.find((item) => item.id === competitionId);
    if (!competition) continue;

    const playerIds = [
      ...new Set(entryRegistrations.map((registration) => registration.tournament_player_id!)),
    ];
    for (const playerId of playerIds) {
      result[competitionId][playerId] = {
        entryId,
        teammateNames: playerIds
          .filter((id) => id !== playerId)
          .map((id) => playersById[id]?.name || "")
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
        playerCount: playerIds.length,
        requiredPlayers: competition.requiredPlayersPerEntry,
        hasPlayed: !!entriesById[entryId]?.last_match_completed_at,
      };
    }
  }

  return result;
}

export class TournamentEntryFormController extends FormController<
  TournamentEntry,
  EntryAttributes,
  State,
  Props
> {
  static override initialState = {
    ...FormController.initialState,
    competitions: new List<TournamentCompetition>(),
    occupationsByCompetition: {},
    players: [],
    selectedPlayers: [],
    searchValue: null,
    pendingOccupiedPlayer: null,
    pendingRemovals: new Set<string>(),
    formatDataSchema: [],
    availableGroups: [],
    originalDivision: undefined,
    groupStageComplete: false,
    playoffsHaveStarted: false,
  };

  override async buildRecord(): Promise<TournamentEntry> {
    return new TournamentEntry({
      tournament_competition_id: this.props.attributes?.tournament_competition_id,
      seed: this.props.attributes?.seed,
      name: this.props.attributes?.name,
      format_data: this.props.attributes?.format_data,
    });
  }

  fields() {
    const competitionId = this.state.record?.tournament_competition_id;
    const competition = competitionId ? this.state.competitions.findById(competitionId) : null;
    const isRoundRobin = competition?.format === "RoundRobin";
    const showGroupField = isRoundRobin;
    const playoffSettings = roundRobinPlayoffSettings(competition);
    const hasDivision = this.state.record?.division !== undefined;
    const showDivisionField =
      isRoundRobin &&
      playoffSettings.enablePlayoffs &&
      playoffSettings.divisions > 1 &&
      (this.state.groupStageComplete || hasDivision);

    const fields: Record<string, any> = {
      tournament_competition_id: field("tournament_competition_id", "select", {
        label: "Competition",
        required: true,
        group: "basic",
        options: this.state.competitions.map((competition) => ({
          label: competition.name || "",
          value: competition.id!,
        })),
        onChange: (value: string) => {
          this.state.record.tournament_competition_id = value;
          this.updateFormatDataSchema();
          this.updateAvailableGroups();
          void this.updatePlayoffDivisionFlags();
        },
      }),

      withdrawn: field("withdrawn", "checkbox", {
        label: "Withdraw",
        group: "basic",
        buttonVariant: {
          checkedLabel: "Re-enter",
          uncheckedLabel: "Withdraw",
          checkedKind: "accent",
          uncheckedKind: "danger",
        },
      }),

      seed: field("seed", "select", {
        label: "Seed",
        required: false,
        group: "basic",
        options: [
          { label: "No seed", value: "" },
          ...Array.from({ length: 16 }, (_, i) => ({
            label: String(i + 1),
            value: String(i + 1),
          })),
        ],
        description: "Seed determines the player's starting position in the competition",
      }),

      players: field("players", "custom", {
        label: "Players",
        required: true,
        group: "players",
        component: PlayerSelectionField,
        value() {
          return null;
        },
      }),
    };

    // Add group field for round robin format
    if (showGroupField) {
      fields.group = field("group", "custom", {
        label: "Group",
        required: false,
        group: "basic",
        description: "The group this entry belongs to",
        component: GroupSelectionField,
        value() {
          return null;
        },
      });
    }

    // Add division field for round robin format with playoffs
    if (showDivisionField) {
      fields.division = field("division", "custom", {
        label: "Division",
        required: false,
        group: "basic",
        description: "The division this entry belongs to (for playoffs)",
        component: DivisionSelectionField,
        value() {
          return null;
        },
      });
    }

    return fields;
  }

  override findModel(id: string) {
    return TournamentEntry.query().find(id, { noCache: true });
  }

  override async onLoad() {
    // Load competitions for this session
    const competitions = await TournamentCompetition.query()
      .eq("tournament_session_id", this.props.tournamentSessionId)
      .orderBy("created_at", "desc")
      .all();

    // Load all players for this session
    const players = await TournamentPlayer.query()
      .eq("tournament_session_id", this.props.tournamentSessionId)
      .all();

    const registrations = await TournamentRegistration.query()
      .in(
        "tournament_competition_id",
        competitions.map((c) => c.id!),
      )
      .all();
    const entryIds = [
      ...new Set(
        registrations
          .map((registration) => registration.tournament_entry_id)
          .filter((id): id is string => !!id),
      ),
    ];
    const entries = entryIds.length > 0 ? await TournamentEntry.query().in("id", entryIds).all() : [];

    this.state.occupationsByCompetition = buildOccupationsByCompetition(
      competitions,
      players,
      registrations,
      entries,
    );

    if (!this.state.newRecord) {
      await this.state.record.registrations.load();
      // Get the selected players from existing registrations
      const registrations = this.state.record.registrations as Array<
        TournamentRegistration & { tournament_player_id: string }
      >;
      const playerIds = registrations
        .filter((r) => r.tournament_player_id !== undefined && r.tournament_player_id !== null)
        .map((r) => r.tournament_player_id);
      this.state.selectedPlayers = players.filter((p) => playerIds.includes(p.id as string));
    } else {
      this.state.record.registrations.set([]);
    }

    this.state.competitions = new List(...competitions);
    this.state.players = players;
    this.state.selectedPlayers = this.state.selectedPlayers || [];
    this.state.searchValue = null;
    this.state.originalDivision = this.state.record.division ?? undefined;

    // Load format data schema if we have a competition
    if (this.state.record.tournament_competition_id) {
      this.updateFormatDataSchema();
      this.updateAvailableGroups();
      await this.updatePlayoffDivisionFlags();
    }
  }

  updateAvailableGroups() {
    const competitionId = this.state.record.tournament_competition_id;
    if (!competitionId) {
      this.state.availableGroups = [];
      return;
    }

    const competition = this.state.competitions.findById(competitionId);
    if (!competition || competition.format !== "RoundRobin") {
      this.state.availableGroups = [];
      return;
    }

    // Get groups from competition format_data metadata
    const groups = competition.format_data?.groups as Record<string, string[]> | undefined;
    const groupNames = groups ? Object.keys(groups).sort() : [];

    // Always include default group names, plus any existing groups
    const defaultGroups = ["Group 1", "Group 2", "Group 3", "Group 4", "Group 5", "Group 6"];
    const allGroups = [...new Set([...defaultGroups, ...groupNames])].sort();

    // Also include the current entry's group if it's set and not in the list
    const currentGroup = this.state.record.format_data?.group as string | undefined;
    if (currentGroup && !allGroups.includes(currentGroup)) {
      allGroups.push(currentGroup);
      allGroups.sort();
    }

    this.state.availableGroups = allGroups;
  }

  async updatePlayoffDivisionFlags() {
    const competitionId = this.state.record.tournament_competition_id;
    if (!competitionId) {
      this.state.groupStageComplete = false;
      this.state.playoffsHaveStarted = false;
      return;
    }

    const competition = this.state.competitions.findById(competitionId);
    if (!competition || competition.format !== "RoundRobin") {
      this.state.groupStageComplete = false;
      this.state.playoffsHaveStarted = false;
      return;
    }

    const adapter = new TournamentDataAdapter();
    const matches = await adapter.loadProjectedMatches(competition);
    this.state.groupStageComplete = groupStageMatchesComplete(matches);
    this.state.playoffsHaveStarted = playoffMatchesStarted(matches);
  }

  override async onDelete() {
    // Delete registrations for this entry
    await TournamentRegistration.query().eq("tournament_entry_id", this.state.record.id).deleteAll();
  }

  /**
   * Override canDelete to prevent deletion once players have played games
   */
  override get canDelete() {
    // Allow deletion for new records
    if (this.state.newRecord) return true;

    // Prevent deletion if the entry has played any completed matches
    if (this.state.record.last_match_completed_at) {
      return false;
    }

    // Otherwise use the default behavior (check if record can be destroyed)
    return super.canDelete;
  }

  get canAddPlayer() {
    if (!this.state.record.tournament_competition_id) return false;
    const competition = this.state.competitions.findById(this.state.record.tournament_competition_id);
    if (!competition) return false;

    const maxPlayers = competition.entry_type === "doubles" ? 2 : 1;
    return this.state.selectedPlayers.length < maxPlayers;
  }

  async actionSelectPlayer(player: TournamentPlayer) {
    if (!this.canAddPlayer) return;
    if (this.state.selectedPlayers.find((p: TournamentPlayer) => p.id === player.id)) {
      this.state.searchValue = null;
      return;
    }

    const occupation = this.occupationFor(player);
    if (occupation && this.shouldConfirmOccupiedMove(occupation)) {
      this.state.pendingOccupiedPlayer = player;
      this.state.searchValue = null;
      return;
    }

    this.addSelectedPlayer(player);
  }

  actionConfirmMoveOccupiedPlayer() {
    const player = this.state.pendingOccupiedPlayer;
    this.state.pendingOccupiedPlayer = null;
    if (!player || !this.canAddPlayer) return;
    this.addSelectedPlayer(player);
  }

  actionCancelMoveOccupiedPlayer() {
    this.state.pendingOccupiedPlayer = null;
  }

  addSelectedPlayer(player: TournamentPlayer) {
    if (!this.state.selectedPlayers.find((p: TournamentPlayer) => p.id === player.id)) {
      this.state.selectedPlayers = [...this.state.selectedPlayers, player];
    }
    this.state.searchValue = null;
  }

  occupationFor(player: TournamentPlayer) {
    const competitionId = this.state.record.tournament_competition_id;
    if (!competitionId || !player.id) return undefined;
    return this.state.occupationsByCompetition[competitionId]?.[player.id];
  }

  shouldConfirmOccupiedMove(occupation: PlayerOccupation) {
    return occupation.playerCount > 1 || occupation.hasPlayed;
  }

  playerSearchStatus(player: TournamentPlayer) {
    const occupation = this.occupationFor(player);
    if (!occupation) return undefined;
    if (occupation.playerCount < occupation.requiredPlayers) {
      return "Already entered · needs partner";
    }
    if (occupation.teammateNames.length > 0) {
      return `Already entered · with ${occupation.teammateNames.join(", ")}`;
    }
    return "Already entered";
  }

  get suggestedPartners(): TournamentPlayer[] {
    if (!this.canAddPlayer) return [];
    const competitionId = this.state.record.tournament_competition_id;
    if (!competitionId) return [];
    const competition = this.state.competitions.findById(competitionId);
    if (!competition || competition.requiredPlayersPerEntry <= 1) return [];

    const selectedIds = new Set(this.state.selectedPlayers.map((player) => player.id));
    const currentEntryId = this.state.record.id;

    return this.state.players
      .filter((player) => {
        if (!player.id || selectedIds.has(player.id)) return false;
        const occupation = this.state.occupationsByCompetition[competitionId]?.[player.id];
        if (!occupation) return false;
        if (occupation.entryId === currentEntryId) return false;
        return occupation.playerCount < occupation.requiredPlayers;
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  }

  get occupiedMoveMessage() {
    const player = this.state.pendingOccupiedPlayer;
    if (!player) return "";
    const occupation = this.occupationFor(player);
    const name = player.name || "This player";
    const teammates = occupation?.teammateNames.join(" and ");
    if (occupation?.hasPlayed && teammates) {
      return `${name} is already paired with ${teammates}, and that entry has already played matches. Adding them here will remove ${name} from that pair.`;
    }
    if (occupation?.hasPlayed) {
      return `${name} is on an entry that has already played matches. Adding them here will remove them from that entry.`;
    }
    if (teammates) {
      return `${name} is already paired with ${teammates}. Adding them here will leave ${teammates} without a partner.`;
    }
    return `${name} is already on another entry. Adding them here will move them to this entry.`;
  }

  updateFormatDataSchema() {
    const competitionId = this.state.record.tournament_competition_id;
    if (!competitionId) {
      this.state.formatDataSchema = [];
      return;
    }

    const competition = this.state.competitions.findById(competitionId);
    if (!competition || !competition.format) {
      this.state.formatDataSchema = [];
      return;
    }

    try {
      const format = getFormat(competition.format as any);
      this.state.formatDataSchema = format.getEntryDataSchema();
    } catch (error) {
      console.error("Error loading format data schema:", error);
      this.state.formatDataSchema = [];
    }
  }

  actionRemovePlayer(player: TournamentPlayer) {
    this.state.selectedPlayers = this.state.selectedPlayers.filter(
      (p: TournamentPlayer) => p.id !== player.id,
    );
  }

  async actionCreatePlayer(name: string) {
    if (!this.state.record.tournament_competition_id) {
      throw new Error("Please select a competition first");
    }

    const competition = this.state.competitions.findById(this.state.record.tournament_competition_id);
    if (!competition) throw new Error("Competition not found");

    if (!this.canAddPlayer) {
      throw new Error("Cannot add more players to this entry");
    }

    const player = new TournamentPlayer({
      tournament_session_id: this.props.tournamentSessionId,
      name,
      account_id: Account.current!.id,
      club_id: competition.club_id,
    });

    await player.save();
    this.state.players = [...this.state.players, player];
    await this.actionSelectPlayer(player);
  }

  override async saveRecord(entry: TournamentEntry) {
    if (!entry.tournament_competition_id) throw new Error("Competition is required");
    if (this.state.selectedPlayers.length === 0) {
      throw new ValidationError({ registrations: "At least one player is required" });
    }

    const competition = this.state.competitions.findById(entry.tournament_competition_id);
    if (!competition) throw new Error("Competition not found");
    const previousGroup = entry.group;
    const previousDivision = this.state.originalDivision;

    entry.account_id = Account.current!.id;
    entry.club_id = competition.club_id;

    // Ensure format_data is initialized
    if (!entry.format_data) {
      entry.format_data = {};
    }

    await entry.save();

    // For RoundRobin format, sync group with competition's groups metadata
    if (competition.format === "RoundRobin" && entry.id) {
      await syncEntryGroupToCompetition(entry, competition);
      const divisionChanged = previousDivision !== entry.division;
      if (divisionChanged) {
        window.dispatchEvent(
          new CustomEvent("setPlayoffDivision", {
            detail: {
              competitionId: competition.id,
              entryId: entry.id,
              division: entry.division,
            },
          }),
        );
      } else if (previousGroup !== entry.group) {
        window.dispatchEvent(
          new CustomEvent("queueGeneration", {
            detail: { competitionId: competition.id, immediate: true },
          }),
        );
      }
    }

    // Update selected players (this creates new registrations as needed)
    await entry.setPlayers(this.state.selectedPlayers.map((p: TournamentPlayer) => p.id as string));
  }
}

export const GroupSelectionField = ApplicationView(
  ({ record }: CustomComponentFieldProps<TournamentEntry>) => {
    const controller = TournamentEntryFormController.use();
    const { saving, availableGroups, competitions } = controller.state;
    const currentGroup = record.format_data?.group || "";

    if (!record.tournament_competition_id) {
      return <p className="text-gray-500">Please select a competition first</p>;
    }

    const competition = competitions.findById(record.tournament_competition_id);
    const groupsLocked = roundRobinGroupsLocked(competition);

    return (
      <div className="space-y-1">
        <Select
          id="group"
          value={currentGroup}
          onChange={(e) => {
            const value = e.target.value || undefined;
            console.log("[GroupWrite][TournamentEntryForm.GroupSelectionField.onChange]", {
              entryId: record.id,
              competitionId: record.tournament_competition_id,
              previousGroup: record.group,
              nextGroup: value,
            });
            record.group = value;
          }}
          disabled={saving}
        >
          <option value="">No group</option>
          {availableGroups.map((group) => (
            <option key={group} value={group}>
              {group}
            </option>
          ))}
        </Select>
        {groupsLocked && (
          <p className="text-xs text-gray-500">
            Groups are locked. Changing this entry still updates matches.
          </p>
        )}
      </div>
    );
  },
);

export const DivisionSelectionField = ApplicationView(
  ({ record }: CustomComponentFieldProps<TournamentEntry>) => {
    const controller = TournamentEntryFormController.use();
    const { saving, competitions, playoffsHaveStarted } = controller.state;
    const currentDivision = record.format_data?.division as number | undefined;

    if (!record.tournament_competition_id) {
      return <p className="text-gray-500">Please select a competition first</p>;
    }

    const competition = competitions.findById(record.tournament_competition_id);
    if (!competition) {
      return <p className="text-gray-500">Competition not found</p>;
    }

    const { divisions: numDivisions } = roundRobinPlayoffSettings(competition);

    return (
      <Select
        id="division"
        value={currentDivision?.toString() || ""}
        onChange={(e) => {
          const value = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
          record.division = value;
        }}
        disabled={saving || playoffsHaveStarted}
        title={
          playoffsHaveStarted
            ? "Playoff divisions cannot be changed after playoffs have started"
            : "The division this entry belongs to (for playoffs)"
        }
      >
        <option value="">None</option>
        {Array.from({ length: numDivisions }, (_, i) => i + 1).map((divisionNum) => (
          <option key={divisionNum} value={divisionNum.toString()}>
            Division {divisionNum}
          </option>
        ))}
      </Select>
    );
  },
);

export const PlayerSelectionField = ApplicationView(({ record }: CustomComponentFieldProps) => {
  const controller = TournamentEntryFormController.use();
  const { saving, players, selectedPlayers, searchValue, errors, loading, pendingOccupiedPlayer } =
    controller.state;

  if (loading) return null;

  if (!record.tournament_competition_id) {
    return <p className="text-gray-500">Please select a competition first</p>;
  }

  const selectedIds = new Set(selectedPlayers.map((player) => player.id));
  const searchablePlayers = players.filter((player) => !selectedIds.has(player.id));
  const suggestedPartners = controller.suggestedPartners;

  return (
    <div className="w-full">
      {controller.canAddPlayer && (
        <LabelledField label="Add player" htmlFor="player_search" className="w-full text-sm medium:text-sm">
          <TournamentPlayerSearch
            players={searchablePlayers}
            value={searchValue}
            onSelect={(player) => controller.actionSelectPlayer(player)}
            onRegister={(name) => controller.actionCreatePlayer(name)}
            playerStatus={(player) => controller.playerSearchStatus(player)}
            tournamentId={record.tournament_competition_id}
            error={errors.registrations}
            disabled={!controller.canAddPlayer || saving}
          />
        </LabelledField>
      )}

      {suggestedPartners.length > 0 && (
        <div className="w-full mt-4" data-testid="suggested-partners">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: old */}
          <label className="block mb-2 text-sm font-medium">Suggested partners</label>
          <div className="flex flex-wrap gap-2">
            {suggestedPartners.map((player) => (
              <Button
                key={player.id}
                type="button"
                kind="secondary"
                onClick={() => controller.actionSelectPlayer(player)}
                disabled={saving}
              >
                {player.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {selectedPlayers.length > 0 && (
        <div className="w-full mt-4">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: old */}
          <label className="block mb-2 text-sm font-medium">Selected players</label>
          <div className="space-y-2">
            {selectedPlayers.map((player: TournamentPlayer) => (
              <div
                key={player.id}
                className="flex items-center justify-between p-2 border border-gray-300 rounded"
              >
                <div>
                  <Button
                    type="button"
                    kind="link"
                    onClick={() => {
                      return routeService.openModal("/tournament/:id/players/:playerId/edit", {
                        params: {
                          id: player.tournament_session_id!,
                          playerId: player.id!,
                        },
                      });
                    }}
                  >
                    {player.name}
                  </Button>
                  {player.email && <div className="text-sm text-gray-500">{player.email}</div>}
                  {player.mobile && <div className="text-sm text-gray-500">{player.mobile}</div>}
                </div>
                <Button
                  type="button"
                  kind="secondary"
                  onClick={() => controller.actionRemovePlayer(player)}
                  disabled={saving}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingOccupiedPlayer && (
        <ModalAlert
          title="Move player to this entry?"
          message={controller.occupiedMoveMessage}
          confirmText="Move player"
          cancelText="Cancel"
          onConfirm={() => controller.actionConfirmMoveOccupiedPlayer()}
          onCancel={() => controller.actionCancelMoveOccupiedPlayer()}
        />
      )}
    </div>
  );
});
